// ============================================================================
// GST MODULE ENDPOINTS
// ============================================================================
// Every GST route sits under the `/gst` prefix — mandatory from the first
// commit (ADR-0008 §5). Path-based routing is what keeps a future
// one-artifact-multiple-instances deployment a gateway configuration change
// rather than a refactor, and retrofitting prefixes across a built module is
// the kind of tedious work that never gets done.

import { buildEndpoint } from './base.endpoints';

const BASE_PATH = '/gst';

export const gstEndpoints = {
  basePath: () => BASE_PATH,

  /**
   * Reports how the GST module is wired for the calling tenant.
   * Returns 404 — not 403 — when the module is not enabled, so a tenant
   * without GST does not learn that it exists.
   */
  moduleInfo: () => buildEndpoint(`${BASE_PATH}/module-info`),

  // ---- Engagements (G-1 slice 1) ----------------------------------------
  engagements: () => buildEndpoint(`${BASE_PATH}/engagements`),
  engagementsBasePath: () => `${BASE_PATH}/engagements`,
  filingPeriods: (id: string) => buildEndpoint(`${BASE_PATH}/engagements/${id}/filing-periods`),
  generateFilingPeriods: (id: string) => buildEndpoint(`${BASE_PATH}/engagements/${id}/filing-periods/generate`),
  recomputeDueDates: (id: string) => buildEndpoint(`${BASE_PATH}/engagements/${id}/filing-periods/recompute-due-dates`),
  workflowDefinitionStatus: () => buildEndpoint(`${BASE_PATH}/workflow-definitions/status`),
  installWorkflowDefinitions: () => buildEndpoint(`${BASE_PATH}/workflow-definitions/install`),
  engagementById: (id: string) => buildEndpoint(`${BASE_PATH}/engagements/${id}`),

  // ---- Editing an engagement (G-1 slice 2f) ------------------------------
  // Widening scope re-runs generation (idempotent); narrowing it strands
  // obligations, which the reconciliation reports rather than removes.
  engagementRegistrations: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/registrations`),
  engagementRegistrationById: (id: string, registrationId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/registrations/${registrationId}`),
  engagementRegistrationWithdraw: (id: string, registrationId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/registrations/${registrationId}/withdraw`),
  engagementFilingOptions: (id: string, registrationId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/registrations/${registrationId}/filing-options`),
  // Which quarters a frequency change may start from. Read-only, and only an
  // echo of the rule — the POST above enforces it regardless.
  engagementElectableQuarters: (id: string, registrationId: string) =>
    buildEndpoint(
      `${BASE_PATH}/engagements/${id}/registrations/${registrationId}/electable-quarters`),
  engagementScopeReconciliation: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/scope-reconciliation`),
  engagementCancelObligation: (id: string, obligationId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/obligations/${obligationId}/cancel`),
  // Carries no ids — the server re-derives what is superseded and acts on that.
  engagementCancelSuperseded: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/obligations/cancel-superseded`),

  // ---- Source documents (ADR-0025 §3) ---------------------------------------
  // Receiving only: the bytes go to object storage and the row records what was
  // received. Parsing, mapping and submission are separate steps.
  engagementSourceDocumentsBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/source-documents`,
  engagementSourceDocuments: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-documents`),
  engagementSourceDocumentById: (id: string, documentId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-documents/${documentId}`),
  engagementSourceDocumentDownload: (id: string, documentId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-documents/${documentId}/download`),

  // ---- Submissions and staging (ADR-0025 §3, §4, §8) ------------------------
  // A submission is what a document FEEDS: one (GSTIN, period, branch, stream),
  // parsed to staging. Creating one is parsing it.
  engagementSubmissionsBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/submissions`,
  engagementSubmissions: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions`),
  engagementSubmissionScopeCheckBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/submissions/scope-check`,
  engagementSubmissionSheetsBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/submissions/sheets`,
  engagementSubmissionBatch: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/batch`),
  engagementSubmissionBranchesBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/submissions/branches`,
  engagementSubmissionById: (id: string, submissionId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/${submissionId}`),
  // Mapping (ADR-0025 §5): candidates and preview are read-only; PUT records the profile.
  engagementSubmissionMappingCandidates: (id: string, submissionId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/${submissionId}/mapping/candidates`),
  engagementSubmissionMappingPreview: (id: string, submissionId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/${submissionId}/mapping/preview`),
  engagementSubmissionMappingProfile: (id: string, submissionId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/${submissionId}/mapping/profile`),
  // The mapper saves a client's learned profile, PROPOSED; a reviewer accepts or rejects it.
  engagementSubmissionMappingProfiles: (id: string, submissionId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/submissions/${submissionId}/mapping/profiles`),
  engagementMappingProfileAccept: (id: string, profileId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/mapping-profiles/${profileId}/accept`),
  engagementMappingProfileReject: (id: string, profileId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/mapping-profiles/${profileId}/reject`),
  mappingFields: () => buildEndpoint(`${BASE_PATH}/mapping-fields`),
  mappingTemplates: () => buildEndpoint(`${BASE_PATH}/mapping-templates`),
  mappingTemplate: (documentType: string) => buildEndpoint(`${BASE_PATH}/mapping-templates/${documentType}`),
  engagementSubmissionStagingBasePath: (id: string, submissionId: string) =>
    `${BASE_PATH}/engagements/${id}/submissions/${submissionId}/staging`,

  // ---- Expected sources and completeness (ADR-0025 §7) ----------------------
  // Completeness is computed on every read; a waiver excuses one source for
  // one return period, with a reason.
  engagementExpectedFiles: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/expected-files`),
  engagementExpectedFileById: (id: string, fileId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/expected-files/${fileId}`),
  engagementExpectedFileSuggestedPeriod: (id: string, fileId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/expected-files/${fileId}/suggested-period`),
  // Receive as an expected file for a period — parses in the same action when the layout is known (R1.2).
  engagementSourceDocumentReceive: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-documents/receive`),
  engagementSourceCompleteness: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-completeness`),
  engagementPeriodCompleteness: (id: string, obligationId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-completeness/${obligationId}`),
  engagementSourceWaivers: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-waivers`),
  engagementSourceWaiverById: (id: string, obligationId: string, waiverId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-completeness/${obligationId}/waivers/${waiverId}`),

  // ---- Source setup (ADR-0025 R1.4) -----------------------------------------
  // Samples are received through `engagementSourceDocumentReceive` with
  // purpose SAMPLE and parsed through the ordinary batch endpoint.
  engagementSetupSamples: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-setup/samples`),
  engagementSetupSampleParses: (id: string, documentId: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-setup/samples/${documentId}/parses`),
  engagementSetupAnalysisBasePath: (id: string) =>
    `${BASE_PATH}/engagements/${id}/source-setup/analysis`,
  engagementSetupFinalise: (id: string) =>
    buildEndpoint(`${BASE_PATH}/engagements/${id}/source-setup/finalise`),

  // ---- Statutory due-date extensions (ADR-0021 §6, §7) -------------------
  // Recording, previewing and applying are three separate calls. A notification
  // is usually read before anyone is certain it has been understood, and
  // stamping hundreds of deadlines should follow a preview rather than happen
  // as a side effect of typing a reference.
  extensions: () => buildEndpoint(`${BASE_PATH}/extensions`),
  extensionStates: () => buildEndpoint(`${BASE_PATH}/extensions/states`),
  /** Raw path — the caller appends the query via buildEndpointWithQuery. */
  extensionStatutoryDatesPath: () => `${BASE_PATH}/extensions/statutory-dates`,
  extensionPreview: (id: string) => buildEndpoint(`${BASE_PATH}/extensions/${id}/preview`),
  extensionApply: (id: string) => buildEndpoint(`${BASE_PATH}/extensions/${id}/apply`),
  extensionRevoke: (id: string) => buildEndpoint(`${BASE_PATH}/extensions/${id}/revoke`),

  // ---- Previous-year aggregate turnover (ADR-0021 §4) --------------------
  // Per CLIENT, not per registration: aggregate turnover is computed across all
  // GSTINs of a PAN, all-India.
  turnover: () => buildEndpoint(`${BASE_PATH}/turnover`),
  turnoverBasePath: () => `${BASE_PATH}/turnover`,
  turnoverById: (id: string) => buildEndpoint(`${BASE_PATH}/turnover/${id}`),
  turnoverByClient: (clientId: string) => buildEndpoint(`${BASE_PATH}/turnover/client/${clientId}`),
  /** Raw path — the caller appends financialYear via buildEndpointWithQuery. */
  turnoverApplicabilityPath: (clientId: string) =>
    `${BASE_PATH}/turnover/client/${clientId}/applicability`,

  /**
   * GSTIN registrations on the client master, flagged with whether GST already
   * extends them and whether they are already in scope for `settingsId`.
   */
  availableRegistrations: (clientId: string) =>
    buildEndpoint(`${BASE_PATH}/clients/${clientId}/available-registrations`),
} as const;

export default gstEndpoints;
